import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  MINDTAP_MAX_KEYS,
  normalizeMindtapReleaseRows,
  validateMindtapReleaseRows,
  type MindtapReleaseKeyRow,
} from '@/lib/mindtapReleaseKeys';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentName: string;
  mode: 'release' | 'update';
  initialRows?: MindtapReleaseKeyRow[] | null;
  loadingInitial?: boolean;
  onConfirm: (keys: MindtapReleaseKeyRow[]) => Promise<void>;
};

const EMPTY_ROW: MindtapReleaseKeyRow = { name: '', courseKey: '' };

export function MindtapReleaseDialog({
  open,
  onOpenChange,
  studentName,
  mode,
  initialRows,
  loadingInitial = false,
  onConfirm,
}: Props) {
  const [rows, setRows] = useState<MindtapReleaseKeyRow[]>([{ ...EMPTY_ROW }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (loadingInitial) return;
    if (initialRows && initialRows.length > 0) {
      setRows(initialRows.map((row) => ({ ...row })));
    } else {
      setRows([{ ...EMPTY_ROW }]);
    }
    setError(null);
  }, [open, initialRows, loadingInitial]);

  const validationError = useMemo(() => validateMindtapReleaseRows(rows), [rows]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setRows([{ ...EMPTY_ROW }]);
      setError(null);
    }
    onOpenChange(next);
  };

  const updateRow = (index: number, patch: Partial<MindtapReleaseKeyRow>) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const addRow = () => {
    if (rows.length >= MINDTAP_MAX_KEYS) return;
    setRows((prev) => [...prev, { ...EMPTY_ROW }]);
  };

  const removeRow = (index: number) => {
    setRows((prev) => {
      if (prev.length <= 1) return [{ ...EMPTY_ROW }];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSave = async () => {
    const message = validateMindtapReleaseRows(rows);
    if (message) {
      setError(message);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await onConfirm(normalizeMindtapReleaseRows(rows));
      setRows([{ ...EMPTY_ROW }]);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save course keys');
    } finally {
      setSaving(false);
    }
  };

  const isUpdate = mode === 'update';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isUpdate ? 'Update course keys' : 'Release course keys'}</DialogTitle>
          <DialogDescription>
            Enter MindTap names and Cengage course keys for{' '}
            <strong>{studentName}</strong>. The student will see them on or after their program
            start date.
          </DialogDescription>
        </DialogHeader>

        {loadingInitial ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {rows.map((row, index) => (
              <div
                key={`mindtap-key-row-${index}`}
                className="space-y-3 rounded-md border p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">Key {index + 1}</p>
                  {rows.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-muted-foreground"
                      onClick={() => removeRow(index)}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      Remove
                    </Button>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`mindtap-name-${index}`}>MindTap name</Label>
                  <Input
                    id={`mindtap-name-${index}`}
                    value={row.name}
                    onChange={(e) => updateRow(index, { name: e.target.value })}
                    placeholder="e.g. ICD 10"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`mindtap-key-${index}`}>Course key</Label>
                  <Input
                    id={`mindtap-key-${index}`}
                    value={row.courseKey}
                    onChange={(e) => updateRow(index, { courseKey: e.target.value })}
                    placeholder="Enter Cengage course key"
                    autoComplete="off"
                  />
                </div>
              </div>
            ))}

            {rows.length < MINDTAP_MAX_KEYS ? (
              <Button type="button" variant="outline" className="w-full" onClick={addRow}>
                <Plus className="mr-2 h-4 w-4" />
                Add key
              </Button>
            ) : null}

            {(error || validationError) && (
              <p className="text-sm text-destructive">{error || validationError}</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleSave()}
            disabled={saving || loadingInitial || Boolean(validationError)}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isUpdate ? (
              'Save keys'
            ) : (
              'Release'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
